import moment from 'moment';

import { truncate } from 'utils/strings';

import DelayedTrackers from 'app/collections/delayed_trackers';
import DropdownSelection from 'app/models/dropdown_selection';
import DateRange from 'app/models/date_range';
import DelayedTrackerStatusDropdownSelection from 'app/models/delayed_tracker_status_dropdown_selection';

import Base from 'app/views/base';
import LoaderView from 'app/views/loader';
import SelectorView from 'app/views/selector_view';
import DatepickerRangeView from 'app/views/datepicker_range_view';

import { List as ListView, Heading } from 'app/components/list/list';
import TrackingCodeHeadingView from 'app/components/list/item/headings/tracking_code';

import tmpl from './template';
import carrierImgTemplate from './carrier_img';
import deliveryDelayTemplate from './delivery_delay';
import statusAndMessageTemplate from './status_and_message';
import deliveryDateTemplate from './delivery_date';

export const CarrierImgView = Base.extend({
  compiledTemplate: carrierImgTemplate,

  initialize({ carrierName }) {
    this.carrierName = carrierName;
  },

  context() {
    return { carrierName: this.carrierName };
  },
});

export const DeliveryDateView = Base.extend({
  compiledTemplate: deliveryDateTemplate,

  context() {
    const estDeliveryDate = this.model.get('est_delivery_date');
    const originalEstimate = this.model.get('original_estimate');
    return {
      originalEstimate: originalEstimate ? moment(originalEstimate).utc().format('MM-DD') : undefined,
      estDeliveryDate: estDeliveryDate ? moment(estDeliveryDate).utc().format('MM-DD') : undefined,
    };
  },
});

export const DeliveryDelayView = Base.extend({
  compiledTemplate: deliveryDelayTemplate,

  initialize({ absoluteDelay }) {
    this.delayInMinutes = parseInt(absoluteDelay, 10);
  },

  context() {
    return {
      delaySeverity: this.delaySeverity(this.delayInMinutes),
      delayFormatted: this.formatDelay(this.delayInMinutes),
    };
  },

  formatDelay(delayInMinutes) {
    const isNegative = Boolean(Math.sign(delayInMinutes) === -1);
    const delayInMinutesAbsolute = Math.abs(delayInMinutes);

    let stringVal;
    if (delayInMinutesAbsolute < 1380) {
      stringVal = `${Math.ceil(delayInMinutesAbsolute / 60)}H`;
    } else if (delayInMinutesAbsolute >= 1380 && delayInMinutesAbsolute < 1440) {
      stringVal = '1D';
    } else {
      stringVal = `${Math.round((delayInMinutesAbsolute / 1440) * 10) / 10}D`;
    }

    return `${isNegative ? '-' : ''}${stringVal}`;
  },

  delaySeverity(delayInMinutes) {
    if (delayInMinutes > 2 * 1440) return 'heavy';
    if (delayInMinutes < 0) return 'advanced';
    if (Math.ceil(delayInMinutes / 60) === 0) return 'none';
    return 'moderate';
  },
});

export const StatusAndMessageView = Base.extend({
  compiledTemplate: statusAndMessageTemplate,

  context() {
    const trackingDetails = this.model.get('tracking_details');
    const lastMessage = trackingDetails ? trackingDetails.last().get('message') : '';
    return {
      status: this.model.get('status'),
      lastMessage: truncate(lastMessage, 20),
    };
  },
});

export const buildHeadings = () => {
  return [
    new Heading({
      subView: m => new CarrierImgView({ carrierName: m.get('carrier') }),
      display: 'Carrier',
      smSize: 1,
      xsHidden: true,
    }),
    new Heading({
      subView: m => new DeliveryDateView({ model: m }),
      display: 'Delivery Date',
      smSize: 2,
      xsSize: 3,
    }),
    new Heading({
      subView: m => new DeliveryDelayView({ absoluteDelay: m.get('absolute_transit_delay') }),
      display: 'Transit Delay',
      xsSize: 2,
    }),
    new Heading({
      subView: m => new DeliveryDelayView({ absoluteDelay: m.get('absolute_delivery_delay') }),
      display: 'Delivery Delay',
      xsSize: 2,
    }),
    new Heading({
      subView: m => new TrackingCodeHeadingView({ model: m }),
      display: 'Tracking Code',
      xsSize: 5,
      smSize: 3,
    }),
    new Heading({
      subView: m => new StatusAndMessageView({ model: m }),
      display: 'Status',
      xsHidden: true,
      smSize: 2,
    }),
  ];
};

export default Base.extend({
  compiledTemplate: tmpl,
  className: 'delayed-trackers',

  initialize() {
    this.dateRange = new DateRange({});
    this.carrierSelection = new DropdownSelection({
      choices: [
        { option: '', readable: 'All Carriers' },
        { option: 'usps', readable: 'USPS' },
        { option: 'fedex', readable: 'FedEx' },
        { option: 'ups', readable: 'UPS' },
      ],
      paramKey: 'carrier',
    });
    this.transitDelayAmountSelection = new DropdownSelection({
      choices: [
        { option: '', readable: 'All Transit Estimates' },
        { option: 1, readable: 'All Transit Delays' },
        { option: 60 * 24 * 3, readable: 'Heavy Transit Delays' },
      ],
      paramKey: 'delayed_in_transit_gte',
      staticKey: { delayed_in_transit: true },
    });
    this.deliveryDelayAmountSelection = new DropdownSelection({
      choices: [
        { option: '', readable: 'All Delivery Estimates' },
        { option: 1, readable: 'All Delivery Delays' },
        { option: 60 * 24 * 3, readable: 'Heavy Delivery Delays' },
      ],
      paramKey: 'delayed_in_delivery_gte',
      staticKey: { delayed_in_delivery: true },
    });
    this.trackerStatusSelection = new DelayedTrackerStatusDropdownSelection({});
    this.collection = new DelayedTrackers([], {
      dateRange: this.dateRange,
      carrierSelection: this.carrierSelection,
      transitDelayAmountSelection: this.transitDelayAmountSelection,
      deliveryDelayAmountSelection: this.deliveryDelayAmountSelection,
      trackerStatusSelection: this.trackerStatusSelection,
    });

    this.listenTo(this.collection, 'update', this.render);

    this.addSubview('daterangePickers', '#daterange_pickers', new DatepickerRangeView({ model: this.dateRange }));
    this.addSubview('carrierSelector', '#carrier_selector', new SelectorView({ model: this.carrierSelection }));
    this.addSubview('transitDelayAmountSelector', '#transit_delay_amount_selector', new SelectorView({ model: this.transitDelayAmountSelection }));
    this.addSubview('deliveryDelayAmountSelector', '#delivery_delay_amount_selector', new SelectorView({ model: this.deliveryDelayAmountSelection }));
    this.addSubview('trackerStatusSelector', '#tracker_status_selector', new SelectorView({ model: this.trackerStatusSelection }));
    this.addSubview('delayedTrackersList', '#delayed_trackers_list', new ListView({
      headings: buildHeadings(),
      collection: this.collection,
      noResultsMessage: 'No delayed trackers have been found matching your filter set.',
    }));
    this.addSubview('loader', '#tracker_loader', new LoaderView({ type: 'flat', collection: this.collection }));
  },
});



// WEBPACK FOOTER //
// ./app/components/pages/delayed_trackers/index.js